import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { ProductOld } from 'src/admin-entity/interface/product/product-old.interface';
import { initialState } from './state';
import * as api from 'src/api/home-display';
import { HomeDisplayCategory } from 'src/admin-entity/interface/home-display/home-display-category.interface';
import { HomeDisplayItem } from 'src/admin-entity/interface/home-display/home-display-item.interface';
import { setSnackbarMessage } from '../snackbarMessage/snackbarSlice';
import { AppState } from 'src/store';
import { getErrorMessage } from 'src/utils/utilService';
import { HomeDisplayBanner } from 'src/admin-entity/interface/home-display/home-display-banner.interface';
import { AutoDisplayProjectType } from 'src/admin-entity/enum/home-display/auto-display-project-type.enum';
import { Category } from 'src/admin-entity/interface/category/category.interface';
import { Distributor } from 'src/admin-entity/interface/distributor/distributor.interface';
import { OrderByType } from 'src/admin-entity/enum/home-display/order-by-type.enum';

export const getMainNavList = createAsyncThunk(
    `homeDisplay/getHomeDisplayCategoryList`,
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const res = await api.getHomeDisplayCategoryList();
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage('목록을 가져오는데 실패했습니다.'));
            return rejectWithValue(err?.response?.data);
        }
    },
);

export const addParentHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/addParentHomeDisplayCategory`,
    async (
        { name, status, navigationType }: { name: string; status: boolean; navigationType: string },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.addParentHomeDisplayCategory(name, status, navigationType);
            console.log(res.data);
            dispatch(setSnackbarMessage('추가되었습니다.'));
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage('추가에 실패했습니다.'));
            return rejectWithValue(err?.response?.data);
        }
    },
);

export const addChildrenHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/addChildrenHomeDisplayCategory`,
    async (parent: HomeDisplayCategory, { rejectWithValue, dispatch }) => {
        try {
            const res = await api.createHomeDisplayCategory({
                name: '임의생성',
                title: '임의생성',
                orderByType: 'manual',
                displayUiType: 1,
                showInformation: true,
                status: false,
                parent,
                displayContentsType: 'product',
                bannerRollingType: 1,
            });
            dispatch(setSnackbarMessage('추가되었습니다.'));
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage('추가에 실패했습니다.'));
            return rejectWithValue(err?.response?.data);
        }
    },
);

export const addTabDisplayCategory = createAsyncThunk(
    `homeDisplay/addTabDisplayCategory`,
    async (ids: number[], { rejectWithValue, getState }) => {
        try {
            const { id } = (getState() as AppState).homeDisplay.selectedHomeDisplayCategory;
            const res = await api.addTabDisplayCategory(id, ids);
            return res.data;
        } catch (err) {
            return rejectWithValue(err?.response?.data);
        }
    },
);

export const getHomeDisplayCategoryDetail = createAsyncThunk(
    `homeDisplay/getHomeDisplayCategoryDetail`,
    async (id: number, { rejectWithValue, dispatch }) => {
        try {
            const res = await api.getHomeDisplayCategoryDetail(id);
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage('정보를 가져오는데 실패했습니다.'));
            return rejectWithValue(err);
        }
    },
);

export const deleteHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/deleteHomeDisplayCategory`,
    async (categoryId: number, { rejectWithValue, dispatch }) => {
        try {
            await api.removeHomeDisplayCategory(categoryId);
            dispatch(setSnackbarMessage('삭제되었습니다.'));
            return categoryId;
        } catch (err) {
            dispatch(setSnackbarMessage('삭제에 실패했습니다.'));
            return rejectWithValue(err);
        }
    },
);

export const updateParentHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/updateParentHomeDisplayCategory`,
    async (
        {
            id,
            name,
            status,
            navigationType,
        }: {
            id: number;
            name: string;
            status: boolean;
            navigationType: string;
        },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.updateParentHomeDisplayCategory(id, name, status, navigationType);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const updateHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/updateHomeDisplayCategory`,
    async (
        {
            category,
            distributor,
            autoDisplayProjectType,
        }: { category?: Category; distributor?: Distributor; autoDisplayProjectType?: AutoDisplayProjectType },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const selectedHomeDisplayCategory = { ...(getState() as AppState).homeDisplay.selectedHomeDisplayCategory };
            selectedHomeDisplayCategory.category = category ?? selectedHomeDisplayCategory.category;
            selectedHomeDisplayCategory.distributor = distributor ?? selectedHomeDisplayCategory.distributor;
            selectedHomeDisplayCategory.autoDisplayProjectType =
                autoDisplayProjectType ?? selectedHomeDisplayCategory.autoDisplayProjectType;
            const res = await api.updateHomeDisplayCategory(selectedHomeDisplayCategory);
            dispatch(setSnackbarMessage('수정되었습니다.'));
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const getHomeDisplayCategoryItems = createAsyncThunk(
    `homeDisplay/getHomeDisplayCategoryProducts`,
    async ({ page, take = 50 }: { page: number; take?: number }, { getState, rejectWithValue }) => {
        try {
            const { id } = (getState() as AppState).homeDisplay.selectedHomeDisplayCategory;
            const res = await api.getHomeDisplayCategoryItems(id, page, take);

            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const getHomeDisplayCategoryTabList = createAsyncThunk(
    `homeDisplay/getHomeDisplayCategoryTabList`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const { id } = (getState() as AppState).homeDisplay.selectedHomeDisplayCategory;
            const res = await api.getHomeDisplayCategoryTabList(id);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const upateHomeDisplayCategoryProductIsPin = createAsyncThunk(
    `homeDisplay/upateHomeDisplayCategoryProductIsPin`,
    async (
        {
            displayCategoryId,
            displayCategoryProductId,
            isPin,
        }: { displayCategoryId: number; displayCategoryProductId: number; isPin: boolean },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.updateHomeDisplayCategoryProductIsPin(
                displayCategoryId,
                displayCategoryProductId,
                isPin,
            );
            if (res.data) {
                dispatch(setSnackbarMessage('고정되었습니다.'));
                return { productId: displayCategoryProductId, isPin };
            } else {
                dispatch(setSnackbarMessage('고정에 실패했습니다.'));
            }
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const addProductsInHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/addProductsInHomeDisplayCategory`,
    async (
        { homeDisplayCategoryId, products }: { homeDisplayCategoryId: number; products: ProductOld[] },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.addProductsInHomeDisplayCategory(
                homeDisplayCategoryId,
                products.map(p => p.id),
            );
            dispatch(setSnackbarMessage('상품이 추가되었습니다.'));
            if (res.data) {
                return res.data;
            }
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const addVideosInHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/addVideosInHomeDisplayCategory`,
    async (
        { homeDisplayCategoryId, products }: { homeDisplayCategoryId: number; products: ProductOld[] },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.addProductsInHomeDisplayCategory(
                homeDisplayCategoryId,
                products.map(p => p.id),
            );
            dispatch(setSnackbarMessage('영상이 추가되었습니다.'));
            if (res.data) {
                return res.data;
            }
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const addBannersInHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/addBannersInHomeDisplayCategory`,
    async (
        { homeDisplayCategoryId, banners }: { homeDisplayCategoryId: number; banners: HomeDisplayBanner[] },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.addBannersInHomeDisplayCategory(
                homeDisplayCategoryId,
                banners.map(p => p.id),
            );
            dispatch(setSnackbarMessage('배너가 추가되었습니다.'));
            if (res.data) {
                return res.data;
            }
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const removeBannersInHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/removeBannersInHomeDisplayCategory`,
    async (
        { homeDisplayCategoryId, bannerIds }: { homeDisplayCategoryId: number; bannerIds: number[] },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.removeBannersInHomeDisplayCategory(homeDisplayCategoryId, bannerIds);
            if (res.data) {
                dispatch(setSnackbarMessage('삭제되었습니다.'));
                return bannerIds;
            } else {
                dispatch(setSnackbarMessage('삭제 중 문제가 발생했습니다.'));
            }
        } catch (err) {
            dispatch(setSnackbarMessage('삭제 중 문제가 발생했습니다.'));
            return rejectWithValue(err);
        }
    },
);

export const removeProductsInHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/removeProductsInHomeDisplayCategory`,
    async (
        { homeDisplayCategoryId, productIds }: { homeDisplayCategoryId: number; productIds: number[] },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.removeProductsInHomeDisplayCategory(homeDisplayCategoryId, productIds);
            if (res.data) {
                dispatch(setSnackbarMessage('삭제되었습니다.'));
                return productIds;
            } else {
                dispatch(setSnackbarMessage('삭제 중 문제가 발생했습니다.'));
            }
        } catch (err) {
            dispatch(setSnackbarMessage('삭제 중 문제가 발생했습니다.'));
            return rejectWithValue(err);
        }
    },
);

export const removeTabDisplayCategory = createAsyncThunk(
    `homeDisplay/removeTabDisplayCategory`,
    async (
        { homeDisplayCategoryId, tabIds }: { homeDisplayCategoryId: number; tabIds: number[] },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await api.removeTabDisplayCategory(homeDisplayCategoryId, tabIds);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const changePriorityProductsInHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/changePriorityProductsInHomeDisplayCategory`,
    async (
        {
            homeDisplayCategoryId,
            priorities,
        }: { homeDisplayCategoryId: number; priorities: { id: number; priority: number }[] },
        { rejectWithValue },
    ) => {
        try {
            const res = await api.changePriorityProductsInHomeDisplayCategory(homeDisplayCategoryId, priorities);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const changePriorityTabList = createAsyncThunk(
    `homeDisplay/changePriorityTabList`,
    async (
        {
            homeDisplayCategoryId,
            priorities,
        }: { homeDisplayCategoryId: number; priorities: { id: number; priority: number }[] },
        { rejectWithValue },
    ) => {
        try {
            const res = await api.changePriorityTabList(homeDisplayCategoryId, priorities);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const changeHomeDisplayChildrenCategoryPriority = createAsyncThunk(
    `homeDisplay/changeHomeDisplayCategoryPriority`,
    async ({ direction }: { direction: 'up' | 'down' }, { dispatch, rejectWithValue, getState }) => {
        try {
            const homeDisplayList = (getState() as AppState).homeDisplay.homeDisplayCategoryList;
            const selectedHDCategory = (getState() as AppState).homeDisplay.selectedHomeDisplayCategory;
            const parentIndex = homeDisplayList.findIndex(item => item.id === selectedHDCategory.parent.id);
            const parent = { ...homeDisplayList[parentIndex] };
            const children = [...parent.children];
            const index = parent.children.findIndex(item => item.id === selectedHDCategory.id);
            const targetIndex = direction === 'up' ? index - 1 : index + 1;
            const temp = children[targetIndex];
            children[targetIndex] = children[index];
            children[index] = temp;

            parent.children = children.map((item, index) => {
                return {
                    ...item,
                    priority: index,
                };
            });

            await api.changeDisplayCategoryPriority(
                parent.children.map(item => {
                    return {
                        id: item.id,
                        priority: item.priority,
                    };
                }),
            );
            const result = [...homeDisplayList];
            result[parentIndex] = parent;
            return result;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const updatePriorityHomeDisplayMainList = createAsyncThunk(
    `homeDisplay/updatePriorityHomeDisplayMainList`,
    async ({ priorities }: { priorities: { id: number; priority: number }[] }, { rejectWithValue, dispatch }) => {
        try {
            const res = await api.changeDisplayCategoryPriority(priorities);
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const updatePriorityChildrenHomeDisplayList = createAsyncThunk(
    `homeDisplay/updatePriorityChildrenHomeDisplayList`,
    async (changeData: HomeDisplayCategory[], { rejectWithValue, dispatch }) => {
        try {
            await api.changeDisplayCategoryPriority(
                changeData.map((item, index) => {
                    return {
                        id: item.id,
                        priority: index,
                    };
                }),
            );
            return changeData;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const addHomeDisplayCategoryImage = createAsyncThunk(
    `homeDisplay/addHomeDisplayCategoryImage`,
    async (
        {
            file,
            setProgress,
            type,
            imageSize,
        }: {
            file: File;
            setProgress: (num: number) => void;
            type: 'detailImage' | 'titleImage' | 'buttonImage';
            imageSize: string;
        },
        { dispatch, rejectWithValue, getState },
    ) => {
        try {
            const { id } = (getState() as AppState).homeDisplay.selectedHomeDisplayCategory;

            const formData = new FormData();
            formData.append(`imageFiles`, file);
            formData.append('displayCategoryImageType', type);
            formData.append('imageSize', imageSize);
            await api.addHomeDisplayCategoryImage(id, formData, setProgress);
            dispatch(setSnackbarMessage('이미지를 등록하였습니다.'));
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const removeHomeDisplayCategoryImage = createAsyncThunk(
    `homeDisplay/removeHomeDisplayCategoryImage`,
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const { selectedHomeDisplayCategory } = (getState() as AppState).homeDisplay;
            await api.removeHomeDisplayCategoryImage(
                selectedHomeDisplayCategory.id,
                selectedHomeDisplayCategory.displayCategoryImages[0].id,
            );
            dispatch(setSnackbarMessage('이미지를 삭제하였습니다.'));
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(err);
        }
    },
);

export const directAddHomeDisplayCategory = createAsyncThunk(
    `homeDisplay/directAddHomeDisplayCategory`,
    async (
        {
            orderByType,
            autoDisplayDays,
            autoDisplayProjectType,
            categoryId,
            distributorId,
            productTagGroupId,
        }: {
            orderByType: OrderByType;
            autoDisplayDays: number;
            autoDisplayProjectType: AutoDisplayProjectType;
            categoryId: number;
            distributorId: number;
            productTagGroupId: number;
        },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = (getState() as AppState).homeDisplay.selectedHomeDisplayCategory.id;
            const res = await api.directAddHomeDisplayCategory(
                id,
                orderByType,
                autoDisplayDays,
                autoDisplayProjectType,
                categoryId,
                distributorId,
                productTagGroupId,
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const homeDisplaySlice = createSlice({
    name: 'homeDisplaySlice',
    initialState,
    reducers: {
        initHomeDisplayCategory: () => {
            return initialState;
        },
        initAutoDisplay: state => {
            state.selectedHomeDisplayCategory.category = { id: 0, name: '선택안함' };
            state.selectedHomeDisplayCategory.distributor = { id: 0, name: '선택안함' };
            state.selectedHomeDisplayCategory.autoDisplayProjectType = null;
        },
        selectParentHomeDisplayCategory: (state, action: PayloadAction<HomeDisplayCategory>) => {
            state.selectedParentHomeDisplayCategory = action.payload;
            state.selectedHomeDisplayCategory = initialState.selectedHomeDisplayCategory;
        },
        setSelectedHomeDisplayProducts: (state, action: PayloadAction<HomeDisplayItem[]>) => {
            state.selectedHomeDisplayCategoryItems.list = action.payload;
        },
        // addHomeDisplayProducts: (state, action: PayloadAction<HomeDisplayProduct[]>) => {
        //     state.selectedHomeDisplayCategoryProducts.list = action.payload;
        // },
        updateSelectedHomeDisplayCategoryName: (state, action: PayloadAction<string>) => {
            state.selectedHomeDisplayCategory.name = action.payload;
        },
        updateSelectedHomeDisplayCategoryTitle: (state, action: PayloadAction<string>) => {
            state.selectedHomeDisplayCategory.title = action.payload;
        },
        updateSelectedHomeDisplayCategorySubTitle: (state, action: PayloadAction<string>) => {
            state.selectedHomeDisplayCategory.subTitle = action.payload;
        },
        updateSelectedHomeDisplayCategoryStatus: (state, action: PayloadAction<boolean>) => {
            state.selectedHomeDisplayCategory.status = action.payload;
        },
        updateSelectedHomeDisplayCategoryContentsType: (
            state,
            action: PayloadAction<HomeDisplayCategory['displayContentsType']>,
        ) => {
            state.selectedHomeDisplayCategory.displayContentsType = action.payload;
        },
        updateSelectedHomeDisplayCategoryTabType: (state, action: PayloadAction<HomeDisplayCategory['tabType']>) => {
            state.selectedHomeDisplayCategory.tabType = action.payload;
        },
        updateSelectedHomeDisplayCategoryOrderByType: (state, action: PayloadAction<OrderByType>) => {
            state.selectedHomeDisplayCategory.orderByType = action.payload;
        },
        updateSelectedHomeDisplayCategoryAutoDisplayProjectType: (
            state,
            action: PayloadAction<AutoDisplayProjectType>,
        ) => {
            state.selectedHomeDisplayCategory.autoDisplayProjectType = action.payload;
        },
        updateSelectedHomeDisplayCategoryBannerType: (state, action: PayloadAction<'normal' | 'detail'>) => {
            state.selectedHomeDisplayCategory.bannerType = action.payload;
        },
        updateSelectedHomeDisplayCategoryId: (state, action: PayloadAction<number>) => {
            if (state.selectedHomeDisplayCategory.category) {
                state.selectedHomeDisplayCategory.category.id = action.payload;
            } else {
                state.selectedHomeDisplayCategory.category = { id: action.payload };
            }
        },
        updateSelectedHomeDisplayCategoryTag: (state, action: PayloadAction<number>) => {
            state.selectedHomeDisplayCategory.productTagGroup = { id: action.payload };
        },
        updateSelectedHomeDisplayCategoryDisplayUI: (state, action: PayloadAction<number>) => {
            state.selectedHomeDisplayCategory.displayUiType = action.payload as any;
        },
        updateSelectedHomeDisplayCategoryBannerRollingType: (state, action: PayloadAction<number>) => {
            state.selectedHomeDisplayCategory.bannerRollingType = action.payload as any;
        },
        updateSelectedHomeDisplayCategoryShowInformation: (state, action: PayloadAction<boolean>) => {
            state.selectedHomeDisplayCategory.showInformation = action.payload;
        },
        updateSelectedHomeDisplayCategoryAutoDisplayDays: (state, action: PayloadAction<number>) => {
            state.selectedHomeDisplayCategory.autoDisplayDays = action.payload;
        },
        updateSelectedHomeDisplayCategoryExcludeCommingSoon: (state, action: PayloadAction<boolean>) => {
            state.selectedHomeDisplayCategory.excludeCommingSoon = action.payload;
        },
        updateSelectedHomeDisplayCategoryDistributor: (state, action: PayloadAction<{ id: number; name: string }>) => {
            state.selectedHomeDisplayCategory.distributor = {
                id: action.payload.id,
                name: action.payload.name,
            };
        },
        updateSelectedHomeDisplayCategoryShowSoldOut: (state, action: PayloadAction<boolean>) => {
            state.selectedHomeDisplayCategory.showSoldOut = action.payload;
        },
        updateSelectedHomeDisplayCategoryInfoDisplayType: (
            state,
            action: PayloadAction<'priceEmphasisType' | 'retailPriceType'>,
        ) => {
            state.selectedHomeDisplayCategory.infoDisplayType = action.payload;
        },
        updateSelectedParentHomeDisplayCategoryName: (state, action: PayloadAction<string>) => {
            state.selectedParentHomeDisplayCategory.name = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(getMainNavList.fulfilled, (state, { payload }) => {
            state.homeDisplayCategoryList = payload;
        });
        builder.addCase(getHomeDisplayCategoryDetail.fulfilled, (state, { payload }) => {
            state.selectedHomeDisplayCategory = payload;
            // state.selectedHomeDisplayCategoryItems = initialState.selectedHomeDisplayCategoryItems;
            state.originProducts = initialState.originProducts;
        });
        builder.addCase(addParentHomeDisplayCategory.fulfilled, (state, { payload }) => {
            state.homeDisplayCategoryList.push(payload);
        });
        builder.addCase(addChildrenHomeDisplayCategory.fulfilled, (state, { payload }) => {
            const index = state.homeDisplayCategoryList.findIndex(cate => cate.id === payload.parent.id);
            if (state.homeDisplayCategoryList[index].children) {
                state.homeDisplayCategoryList[index].children.unshift(payload);
            } else {
                state.homeDisplayCategoryList[index].children = [payload];
            }
        });
        builder.addCase(getHomeDisplayCategoryItems.fulfilled, (state, { payload }) => {
            if (payload) {
                state.selectedHomeDisplayCategoryItems = payload;
                state.originProducts = payload;
            }
        });
        builder.addCase(addProductsInHomeDisplayCategory.fulfilled, (state, { payload }) => {
            // state.originProducts.list.unshift(...payload);
            // state.selectedHomeDisplayCategoryProducts.list.unshift(...payload);
        });
        builder.addCase(removeProductsInHomeDisplayCategory.fulfilled, (state, { payload }) => {
            state.originProducts.list = state.originProducts.list.filter(
                item => payload.findIndex(p => p === item.product.id) < 0,
            );
            state.selectedHomeDisplayCategoryItems.list = state.selectedHomeDisplayCategoryItems.list.filter(
                item => payload.findIndex(p => p === item.product.id) < 0,
            );
        });
        builder.addCase(changePriorityProductsInHomeDisplayCategory.fulfilled, (state, { payload }) => {
            // const tempSDCP = state.selectedHomeDisplayCategoryProducts.list
            //     .map(dcp => {
            //         const index = payload.findIndex(item => item.id === dcp.id);
            //         if (index >= 0) {
            //             return {
            //                 ...dcp,
            //                 ...payload[index],
            //             };
            //         } else {
            //             return dcp;
            //         }
            //     })
            //     .sort((a, b) => (a.isPin === b.isPin ? a.priority - b.priority || b.id - a.id : 1));
            // state.selectedHomeDisplayCategoryProducts.list = tempSDCP;
            // state.originProducts.list = state.originProducts.list
            //     .map(dcp => {
            //         const index = payload.findIndex(item => item.id === dcp.id);
            //         if (index >= 0) {
            //             return {
            //                 ...dcp,
            //                 ...payload[index],
            //             };
            //         } else {
            //             return dcp;
            //         }
            //     })
            //     .sort((a, b) => (a.isPin === b.isPin ? a.priority - b.priority || b.id - a.id : 1));
        });
        builder.addCase(updateHomeDisplayCategory.fulfilled, (state, { payload }) => {
            const copied = [...current(state).homeDisplayCategoryList];
            state.homeDisplayCategoryList = copied.map(parent => {
                if (parent.id === payload.id) {
                    return {
                        ...payload,
                    };
                } else {
                    return {
                        ...parent,
                        children: parent.children.map(child => {
                            if (child.id === payload.id) {
                                return {
                                    ...payload,
                                };
                            } else {
                                return {
                                    ...child,
                                };
                            }
                        }),
                    };
                }
            });
        });
        builder.addCase(changeHomeDisplayChildrenCategoryPriority.fulfilled, (state, { payload }) => {
            state.homeDisplayCategoryList = payload;
        });
        builder.addCase(changePriorityTabList.fulfilled, (state, { payload }) => {
            // state.selectedHomeDisplayCategoryTabList = payload;
        });
        builder.addCase(getHomeDisplayCategoryTabList.fulfilled, (state, { payload }) => {
            state.selectedHomeDisplayCategoryTabList = payload;
        });
        builder.addCase(addTabDisplayCategory.fulfilled, (state, { payload }) => {
            console.log(payload);
        });
        // builder.addCase(updateHomeDisplayCategory.fulfilled, (state, { payload }) => {
        //     const targetIndex = state.selectedParentHomeDisplayCategory.children.findIndex(
        //         item => item.id === payload.id,
        //     );
        //     state.selectedParentHomeDisplayCategory.children[targetIndex].name = payload.name;
        // });
        builder.addCase(updatePriorityChildrenHomeDisplayList.fulfilled, (state, { payload }) => {
            state.selectedParentHomeDisplayCategory.children = payload;

            const index = state.homeDisplayCategoryList.findIndex(
                item => item.id === state.selectedParentHomeDisplayCategory.id,
            );
            if (index >= 0) {
                state.homeDisplayCategoryList[index].children = payload;
            }
        });
        builder.addCase(deleteHomeDisplayCategory.fulfilled, (state, { payload }) => {
            const isParent = state.homeDisplayCategoryList.findIndex(item => item.id === payload) >= 0;

            if (!isParent) {
                const parentIndex = state.homeDisplayCategoryList.findIndex(
                    item => item.id === state.selectedParentHomeDisplayCategory.id,
                );
                state.homeDisplayCategoryList[parentIndex].children = state.homeDisplayCategoryList[
                    parentIndex
                ].children.filter(item => item.id !== payload);
                state.selectedParentHomeDisplayCategory.children =
                    state.selectedParentHomeDisplayCategory.children.filter(item => item.id !== payload);
            }
        });
        builder.addCase(updatePriorityHomeDisplayMainList.fulfilled, (state, { payload }) => {
            state.homeDisplayCategoryList = payload.map(item => {
                return state.homeDisplayCategoryList.find(cate => cate.id === item.id);
            });
        });
    },
});

export default homeDisplaySlice.reducer;
export const {
    initHomeDisplayCategory,
    initAutoDisplay,
    setSelectedHomeDisplayProducts,
    // addHomeDisplayProducts,
    updateSelectedHomeDisplayCategoryName,
    updateSelectedHomeDisplayCategoryTitle,
    updateSelectedHomeDisplayCategorySubTitle,
    updateSelectedHomeDisplayCategoryStatus,
    updateSelectedHomeDisplayCategoryShowSoldOut,
    updateSelectedHomeDisplayCategoryContentsType,
    updateSelectedHomeDisplayCategoryTabType,
    updateSelectedHomeDisplayCategoryOrderByType,
    updateSelectedHomeDisplayCategoryBannerType,
    updateSelectedHomeDisplayCategoryId,
    updateSelectedHomeDisplayCategoryDisplayUI,
    updateSelectedHomeDisplayCategoryShowInformation,
    updateSelectedHomeDisplayCategoryAutoDisplayDays,
    updateSelectedHomeDisplayCategoryExcludeCommingSoon,
    updateSelectedHomeDisplayCategoryDistributor,
    updateSelectedHomeDisplayCategoryInfoDisplayType,
    updateSelectedHomeDisplayCategoryAutoDisplayProjectType,
    updateSelectedHomeDisplayCategoryBannerRollingType,
    updateSelectedHomeDisplayCategoryTag,
    selectParentHomeDisplayCategory,
    updateSelectedParentHomeDisplayCategoryName,
} = homeDisplaySlice.actions;
